import { Button, Caption, Checkbox, Paragraph, Stack } from '@mediahuis/chameleon-react';
import { Trans, useTranslation } from 'react-i18next';
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBrandGroupConfig } from '../../hooks/useBrandGroupConfig';
import { useQueryParams } from '../../hooks/useQueryParams';
import { useAuthenticationContext } from '../../providers/AuthenticationProvider';
import { subscriptionRoleService, InviteAcceptanceError } from '../../services/srsService';
import { ROUTES } from '../../router/types';
import { OtherAccountButton } from '../../components/OtherAccountButton';
import { useTracking } from '../../hooks/useTracking';
import { EventActionValues, EventCategoryValues, EventLabelValues } from '../../types/tracking';
import { InvitationErrorContent } from '../../components/InvitationErrorContent';

const TERMS_AND_CONDITIONS_URLS: Record<string, string> = {
  default: 'https://www.mediahuis.be/nl/voorwaarden/abonnementsvoorwaarden',
  nl: 'https://www.mediahuis.be/nl/voorwaarden/abonnementsvoorwaarden',
};

function Invitation() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const brandGroupConfigHook = useBrandGroupConfig();
  const { brand, name, inviteId, hasSettled } = useQueryParams();
  const brandConfig = brandGroupConfigHook.getBrandConfigFromBrandId(brand);
  const { user } = useAuthenticationContext();
  const { trackEventWithCategory } = useTracking();

  const locationState = location.state as { errorCode?: string; hasError?: boolean } | null;
  // If location state already carries an error (e.g. coming from /invite-error redirect),
  // skip the eligibility check entirely and show the error screen immediately.
  const shouldSkipEligibilityCheck = !!locationState?.hasError;

  const [errorCode, setErrorCode] = useState<string | null>(locationState?.errorCode || null);
  const [hasError, setHasError] = useState<boolean>(shouldSkipEligibilityCheck);
  const [loading, setLoading] = useState(false);
  const [checkingEligibility, setCheckingEligibility] = useState(!shouldSkipEligibilityCheck);
  const [accepted, setAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  // Track if we've already fired the initial page view event
  const hasTrackedPageView = useRef(false);
  const hasTrackedSuccess = useRef(false);
  // Prevent the eligibility check from running more than once
  const hasRunEligibilityCheck = useRef(false);

  const capitalizedName = name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : name;

  /**
   * Track page view event on component mount (only when not in error state)
   * Uses a ref to ensure this only fires once
   */
  useEffect(() => {
    if (brand && !hasError && !hasTrackedPageView.current) {
      trackEventWithCategory(
        EventActionValues.Show,
        EventLabelValues.InvitationAccept,
        EventCategoryValues.Invitation,
        undefined,
        undefined,
        'invitation_start'
      );
      hasTrackedPageView.current = true;
    }
  }, [brand, hasError, trackEventWithCategory]);

  /**
   * Track success state when invitation is accepted
   * Uses a ref to ensure this only fires once
   */
  useEffect(() => {
    if (accepted && brand && !hasTrackedSuccess.current) {
      trackEventWithCategory(
        EventActionValues.Show,
        EventLabelValues.InvitationCompleted,
        EventCategoryValues.Invitation,
        undefined,
        undefined,
        'invitation_submit'
      );
      hasTrackedSuccess.current = true;
    }
  }, [accepted, brand, trackEventWithCategory]);

  /**
   * Check if the user can accept the invite before showing the accept UI.
   * Waits for useQueryParams to settle (hasSettled flips true after its internal
   * useEffect runs), then checks inviteId — if missing, redirects to the generic
   * error screen immediately regardless of whether brand is present.
   * - If inviteId is missing after params settle → show generic error immediately
   * - 200: show accept UI
   * - 422: show inline error with the returned error code
   * - any other HTTP error: show generic inline error
   * - no response / timeout: fall through and show the accept UI
   */
  useEffect(() => {
    if (shouldSkipEligibilityCheck) return;
    if (!hasSettled) return; // params haven't settled yet
    if (hasRunEligibilityCheck.current) return;
    hasRunEligibilityCheck.current = true;

    if (!inviteId) {
      redirectToError();
      setCheckingEligibility(false);
      return;
    }

    subscriptionRoleService
      .canAcceptInvite(inviteId)
      .then(() => {
        setCheckingEligibility(false);
      })
      .catch((error) => {
        if (error instanceof InviteAcceptanceError && error.statusCode === 422) {
          redirectToError(error.problemDetails?.errorCode);
        } else if (error instanceof InviteAcceptanceError) {
          redirectToError();
        }
        // Network errors / timeouts (no response): fall through and show the accept UI
        setCheckingEligibility(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSettled]);

  // Use alternative copy when name is not provided
  const titleKey = capitalizedName
    ? 'invitation.accept.titleText'
    : 'invitation.accept.titleTextAlternative';
  const descriptionKey = capitalizedName
    ? 'invitation.accept.description'
    : 'invitation.accept.descriptionAlternative';

  const redirectToError = (code?: string) => {
    setErrorCode(code || null);
    setHasError(true);
  };

  const handleAccept = async () => {
    if (!termsAccepted) {
      setShowTermsError(true);
      return;
    }

    // Track accept button click
    trackEventWithCategory(
      EventActionValues.Click,
      EventLabelValues.InvitationAcceptContinue,
      EventCategoryValues.Invitation
    );

    if (!inviteId) {
      redirectToError();
      return;
    }

    setLoading(true);

    try {
      const success = await subscriptionRoleService.acceptInvite(inviteId);
      if (success) {
        setAccepted(true);
      } else {
        redirectToError();
      }
    } catch (error) {
      if (error instanceof InviteAcceptanceError) {
        if (error.statusCode === 500) {
          const searchParams = new URLSearchParams(location.search);
          navigate(`${ROUTES.INTERNAL_ERROR}?${searchParams.toString()}`);
        } else {
          redirectToError(error.problemDetails?.errorCode);
        }
      } else {
        redirectToError();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtherAccountClick = () => {
    // Track other account button click
    trackEventWithCategory(
      EventActionValues.Click,
      EventLabelValues.InvitationAcceptOtherUser,
      EventCategoryValues.Invitation
    );
  };

  const handleStartReadingClick = () => {
    // Track start reading button click
    trackEventWithCategory(
      EventActionValues.Click,
      EventLabelValues.InvitationCompletedContinue,
      EventCategoryValues.Invitation
    );

    window.location.href = brandConfig?.urlDisplay ? `https://${brandConfig.urlDisplay}` : '#';
  };

  return (
    <Stack alignItems="stretch" flexDirection="column" className="bundling-upgrade px-5 m-auto">
      {checkingEligibility ? null : hasError ? (
        <InvitationErrorContent errorCode={errorCode} />
      ) : accepted ? (
        <>
          <Paragraph size="Paragraph" className="bold text-center mt-7 mb-3">
            {t('invitation.success.titleText')}
          </Paragraph>
          <Caption size="lg" className="text-center">
            {t('invitation.success.description', { email: user?.email, brand: brandConfig?.name })}
          </Caption>
          <Caption size="lg" className="text-center mt-3">
            {t('invitation.success.tip')}
          </Caption>
          <Button
            size="lg"
            appearance="primary"
            data-cy="successButton"
            fullWidth
            onClick={handleStartReadingClick}>
            {t('invitation.success.buttonText', { brand: brandConfig?.name })}
          </Button>
        </>
      ) : (
        <>
          <Paragraph size="Paragraph" className="bold text-center mt-7 mb-3">
            <Trans
              i18nKey={titleKey}
              values={{ brand: brandConfig?.name, name: capitalizedName }}
            />
          </Paragraph>
          <Caption size="lg" className="text-center">
            <Trans
              i18nKey={descriptionKey}
              values={{ brand: brandConfig?.name, name: capitalizedName }}
            />
          </Caption>
          <Stack
            flexDirection="column"
            alignItems="center"
            style={{
              backgroundColor: '#f5f5f5',
              padding: '1rem',
              borderRadius: '4px',
            }}>
            <Caption size="lg" className="bold" style={{ marginBottom: '0' }}>
              {t('invitation.accept.loggedInAs')}
            </Caption>
            <Caption size="lg" style={{ marginTop: '0.5rem' }}>
              {user?.email}
            </Caption>
          </Stack>
          <Checkbox
            label={
              <Caption size="lg" style={{ hyphens: 'auto', wordBreak: 'break-word' }}>
                <Trans
                  i18nKey="invitation.accept.termsAndConditions"
                  components={{
                    termsConditions: (
                      <a
                        target="_blank"
                        href={
                          TERMS_AND_CONDITIONS_URLS[i18n.language] ??
                          TERMS_AND_CONDITIONS_URLS.default
                        }
                        rel="noreferrer"
                        style={{ textDecoration: 'underline', color: 'inherit' }}>
                        {''}
                      </a>
                    ),
                  }}
                />
              </Caption>
            }
            checked={termsAccepted}
            id={'invitation-accept-terms'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setTermsAccepted(e.target.checked);
              if (e.target.checked) {
                setShowTermsError(false);
              }
            }}
          />
          {showTermsError && (
            <Caption size="sm" style={{ color: 'red', marginTop: '0.2rem' }}>
              {t('invitation.accept.errorTermsAndConditions')}
            </Caption>
          )}
          <Button
            size="lg"
            appearance="primary"
            data-cy="acceptButton"
            fullWidth
            style={{ marginBottom: '0.7rem' }}
            onClick={handleAccept}
            loading={loading}
            disabled={loading || !termsAccepted}>
            {t('invitation.accept.acceptButtonText')}
          </Button>
          <OtherAccountButton
            disabled={loading}
            style={{ marginTop: '0' }}
            onClick={handleOtherAccountClick}
            reauthenticate
          />
        </>
      )}
    </Stack>
  );
}

export default Invitation;
